import ProductsCreate from "@/components/products/products.create.component.vue";
import ProductsImport from "@/components/products/products.import.component.vue";
import CharacteristicsIndex from "@/components/base-data/characteristics/characteristics.index.component.vue";
import CharacteristicsCreate from "@/components/base-data/characteristics/characteristics.create.component.vue";
import TemplatesIndex from "@/components/base-data/templates/templates.index.component.vue";
import TemplatesCreate from "@/components/base-data/templates/templates.create.component.vue";
import ProductsEdit from "@/components/products/products.edit.component.vue";
import ReportsCreate from "@/components/reports/reports.create.component.vue";
import ReportsShow from "@/components/reports/reports.show.component.vue";
import ReportsConfigure from "@/components/reports/reports.configure.component.vue";
import ReportsEdit from "@/components/reports/reports.edit.component.vue";
import ReportsSearch from "@/components/reports/reports.search.component.vue";
import ReportsView from "@/components/reports/reports.view.component.vue";
import Login from "@/components/login/login.component.vue";
import FileViewer from "@/components/administration/files/file-viewer.vue";
import Portal from "@/components/portal/portal.component.vue";
import TemplateView from "@/components/base-data/templates/templates.view.component.vue";
import FileUpload from "@/components/administration/files/file.upload.component.vue";
import FileOverview from "@/components/administration/files/file.overview.component.vue";
import VueRouter, { RouteConfig } from "vue-router";
import ProductsIndex from "@/components/products/products.index.compoment.vue";
import Dashboard from "@/components/dashboard/dashboard.component.vue";
import ReportsIndex from "@/components/reports/reports.index.component.vue";
import { UserService } from "@/services/UserService";
import { Role } from "@/models/graphql-global-types";
import lodash from "lodash";

interface IRoute extends RouteConfig {
  permittedRoles?: Role[];
}

const portalRoutes: IRoute[] = [
  // @ts-ignore
  {
    path: "/dashboard",
    component: Dashboard,
    name: "/dashboard",
    permittedRoles: [Role.admin, Role.supervisor, Role.tester]
  },
  {
    path: "/reports/manage",
    component: ReportsIndex as any,
    name: "/reports/manage",
    permittedRoles: UserService.getAllRolesAsList()
  },
  {
    path: "/reports/create",
    component: ReportsCreate,
    name: "/reports/create",
    permittedRoles: [Role.admin, Role.supervisor, Role.tester]
  },
  {
    path: "/reports/search",
    component: ReportsSearch,
    name: "/reports/search",
    permittedRoles: [Role.admin, Role.supervisor]
  },
  {
    path: "/reports/view",
    component: ReportsView,
    name: "reports/view",
    permittedRoles: [Role.admin, Role.supervisor, Role.viewer]
  },
  {
    path: "/reports/show/:id",
    component: ReportsShow,
    name: "report/show/:id",
    permittedRoles: UserService.getAllRolesAsList()
  },
  {
    path: "/reports/config/:id",
    component: ReportsConfigure,
    name: "/reports/config/:id",
    permittedRoles: [Role.admin, Role.supervisor]
  },
  {
    path: "/reports/edit/:id",
    component: ReportsEdit,
    name: "/reports/edit/:id",
    permittedRoles: [Role.admin, Role.supervisor, Role.tester]
  },
  { path: "/products", component: ProductsIndex, name: "/products", permittedRoles: [Role.admin, Role.supervisor] },
  {
    path: "/products/edit/:id",
    component: ProductsEdit,
    name: "/products/edit/:id",
    permittedRoles: [Role.admin, Role.supervisor]
  },
  {
    path: "/products/create",
    component: ProductsCreate,
    name: "/products/create",
    permittedRoles: [Role.admin, Role.supervisor]
  },
  {
    path: "/products/import",
    component: ProductsImport,
    name: "/products/import",
    permittedRoles: [Role.admin, Role.supervisor]
  },
  {
    path: "/characteristics",
    component: CharacteristicsIndex,
    name: "characteristics",
    permittedRoles: [Role.admin]
  },
  {
    path: "/characteristics/create",
    component: CharacteristicsCreate,
    name: "characteristic/create",
    permittedRoles: [Role.admin]
  },
  {
    path: "/templates",
    component: TemplatesIndex,
    name: "templates",
    permittedRoles: [Role.admin]
  },
  {
    path: "/templates/create",
    component: TemplatesCreate,
    name: "templates/create",
    permittedRoles: [Role.admin]
  },
  {
    path: "/templates/view/:id",
    component: TemplateView,
    name: "templates/view/:id",
    permittedRoles: [Role.admin, Role.supervisor]
  },
  {
    path: "/show/report/:secret",
    component: ReportsShow,
    name: "show/report/:secret"
  },
  {
    path: "/files/upload",
    component: FileUpload,
    name: "file/upload",
    permittedRoles: [Role.admin]
  },
  {
    path: "/files/overview",
    component: FileOverview,
    name: "file/overview",
    permittedRoles: [Role.admin]
  }
];

export const redirectDefault = {
  path: "*",
  redirect: "/dashboard",
  name: "report-redirect",
  permittedRoles: [Role.admin, Role.supervisor, Role.tester]
};
const redirectViewer = {
  path: "*",
  redirect: "/reports/view",
  name: "report-redirect",
  permittedRoles: [Role.viewer]
};

const publicRoutes: IRoute[] = [
  {
    path: "/",
    redirect: "/dashboard",
    name: "report-redirect-public",
    permittedRoles: UserService.getAllRolesAsList()
  },
  { path: "/login", component: Login, name: "login" },
  { path: "/files", component: FileViewer, name: "file-viewer" }
];

const rawRoutes = [...portalRoutes, ...publicRoutes];

const vueRoutes = [
  ...publicRoutes,
  redirectDefault,
  redirectViewer,
  {
    path: "/",
    component: Portal,
    children: portalRoutes
  }
];

const router = new VueRouter({
  routes: vueRoutes
});

router.beforeEach((to, from, next) => {
  const routeTo = rawRoutes.find(r => r.name === to.name);
  const authRequired = !!lodash.get(routeTo, "permittedRoles", null);

  const { user } = UserService;

  if (authRequired && !user) {
    return next("/login");
  }

  if (user && !user.report_number_prefix) {
    // force user to login again
    sessionStorage.clear();
    return next("/login");
  }

  if (routeTo && !routeTo.permittedRoles) {
    return next();
  }

  if (routeTo && routeTo.permittedRoles && routeTo.permittedRoles.includes(lodash.get(user, "role") as Role)) {
    return next();
  }

  if (UserService.isUserViewer()) {
    return next(redirectViewer.redirect);
  } else {
    return next(redirectDefault.redirect);
  }
});

export default router;
